<!-- src/components/Profile/Candidate/ResumeModal.vue -->
<template>
  <v-dialog v-model="dialog" width="600" persistent>
    <v-card style="border-radius: 10px">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h6 font-weight-bold">Resume</span>
        <v-btn icon size="small" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div class="text-body-2 mb-4 text-grey-darken-1">
          Upload your most recent resume.
        </div>

        <v-card class="upload-card pa-6 mb-6" flat outlined>
          <div
            class="d-flex flex-column align-center justify-center"
            style="height: 120px"
          >
            <v-icon size="40" color="grey-darken-1" class="mb-2"
              >mdi-file-upload-outline</v-icon
            >
            <label class="font-weight-bold text-body-2 pointer">
              <span class="text-primary" @click="triggerFileUpload">
                Click to upload resume
              </span>
              or
              <span class="text-grey-darken-1">drag and drop</span>
              <input
                ref="fileInput"
                type="file"
                class="d-none"
                @change="onFileChange"
                accept=".pdf,.doc,.docx"
              />
            </label>
            <div class="text-caption mt-2 text-grey-darken-1">
              We support a maximum of 5 MB file in .DOC, .DOCX, .PDF formats
            </div>
            <!-- Display either the chosen File.name or the “already saved” filename -->
            <div v-if="form.cv" class="mt-2 text-success">
              <template v-if="form.cv.isMeta">
                {{ form.cv.name }} (already selected)
              </template>
              <template v-else>
                {{ form.cv.name }}
              </template>
            </div>
          </div>
        </v-card>
      </v-card-text>

      <v-card-actions class="d-flex justify-end">
        <v-btn variant="outlined" @click="clearAll">Clear All</v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :disabled="!canSave"
          @click="saveResume"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";
import { useStore } from "vuex";

const props = defineProps({ modelValue: Boolean });
const emit = defineEmits(["update:modelValue"]);

const store = useStore();
const dialog = ref(props.modelValue);

// Keep v-model in sync
watch(() => props.modelValue, (val) => (dialog.value = val));
watch(dialog, (val) => emit("update:modelValue", val));

// Local ref to hold the raw File until final submit:
const resumeFile = ref(null);

// form.cv can be either:
// - a File (the user just selected) 
// - or { name: string, isMeta: true } if loaded from IndexedDB/localStorage
const form = reactive({ cv: null });

const error = ref(false);
const errorMessage = ref("");

// Computed: enable “Save” if we have either a File or metadata
const canSave = computed(() => {
  return form.cv !== null;
});

const fileInput = ref(null);
const triggerFileUpload = () => {
  fileInput.value?.click();
};

const onFileChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const maxSize = 5 * 1024 * 1024; // 5 MB
  if (file.size > maxSize) {
    store.dispatch("alert/error", "File size must be less than 5 MB");
    return;
  }

  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];
  if (!allowedTypes.includes(file.type)) {
    store.dispatch(
      "alert/error",
      "Please upload a valid PDF, DOC, or DOCX file"
    );
    return;
  }

  resumeFile.value = file;
  form.cv = file;  // File instanceof File
  error.value = false;
  errorMessage.value = "";

  // Reset the input so the same file can be re-selected if user changes mind
  event.target.value = null;
};

const clearAll = () => {
  resumeFile.value = null;
  form.cv = null;
  error.value = false;
  errorMessage.value = "";
};

const close = () => {
  dialog.value = false;
};

// Save to Vuex, IndexedDB, and localStorage
async function saveResume() {
  if (!form.cv) {
    error.value = true;
    errorMessage.value = "Please select a resume file first";
    return;
  }

  // If form.cv is a File, use its .name. If it’s meta, use that name too
  const filename = form.cv instanceof File ? form.cv.name : form.cv.name;

  // 1) Save raw File Blob in Vuex
  store.commit("profile/SET_RESUME_BLOB", resumeFile.value);

  // 2) Dispatch action to save it into IndexedDB & metadata → localStorage
  await store.dispatch("profile/saveResumeFile", {
    blob: resumeFile.value,
    filename,
  });

  store.dispatch("alert/success", "Resume saved succesfully");
  dialog.value = false;
}

// On open, prefill from Vuex/localStorage/IndexedDB
watch(
  () => dialog.value,
  async (isOpen) => {
    if (isOpen) {
      const meta = store.state.profile.resumeMeta;
      if (meta && meta.name) {
        // We know a resumeBlob should exist in IndexedDB. 
        // By the time we get here, `profile/loadFromLocalStorage()` already ran
        // and rehydrated `state.profile.resumeBlob` from IndexedDB if possible.
        // We show just the metadata:
        form.cv = { name: meta.name, isMeta: true };
        resumeFile.value = store.state.profile.resumeBlob || null;
      } else {
        form.cv = null;
        resumeFile.value = null;
      }
      error.value = false;
      errorMessage.value = "";
    }
  }
);
</script>

<style scoped>
.upload-card {
  background: #fafbfc !important;
  border-radius: 12px;
  border: 1px dashed #ddd !important;
  min-height: 135px;
  transition: border-color 0.2s ease;
}
.upload-card:hover {
  border-color: #bbb !important;
}
.text-error {
  color: red;
}
</style>
